import {
  canBeat,
  isWildcard,
  legalPlays,
  rankOf,
  type Card,
  type Combo,
  type ComboType,
  type Rank,
  type Ruleset,
} from "@guandan/engine";

export interface SelectionOption {
  readonly cards: readonly Card[];
  readonly combo: Combo;
  readonly active: boolean;
  readonly added: number;
}

const TYPE_ORDER: readonly ComboType[] = [
  "single", "pair", "triple", "fullHouse", "straight",
  "consecutivePairs", "consecutiveTriples", "straightFlush", "bomb", "jokerBomb",
];

function sameCards(a: readonly Card[], b: readonly Card[]): boolean {
  if (a.length !== b.length) return false;
  const right = new Set(b);
  return a.every((card) => right.has(card));
}

function optionKey(option: SelectionOption): string {
  return `${option.combo.type}:${option.combo.rank}:${[...option.cards].sort((a, b) => a - b).join(",")}`;
}

/**
 * Rebind an enumerator representative to the physical cards the player chose.
 *
 * The engine emits one representative per shape, while a double deck may hold
 * several interchangeable copies. Exact cards are preferred; same-rank cards
 * and wildcards may replace a representative slot, with `canBeat` as the final
 * authority over whether the resulting physical set is genuinely playable.
 */
function anchorCombo(
  representative: Combo,
  selected: readonly Card[],
  current: Combo | null,
  level: Rank,
  rules: Ruleset,
): { cards: Card[]; combo: Combo } | null {
  if (selected.length > representative.cards.length) return null;
  const base = [...representative.cards];
  const used = new Set<number>();
  let answer: { cards: Card[]; combo: Combo } | null = null;

  const visit = (selectedIndex: number, cards: Card[]) => {
    if (answer !== null) return;
    if (selectedIndex >= selected.length) {
      if (new Set(cards).size !== cards.length || !selected.every((card) => cards.includes(card))) return;
      const reading = canBeat(cards, current, level, rules);
      if (reading !== null) answer = { cards, combo: reading };
      return;
    }
    const selectedCard = selected[selectedIndex] as Card;
    const exact: number[] = [];
    const compatible: number[] = [];
    for (let slot = 0; slot < cards.length; slot++) {
      if (used.has(slot)) continue;
      const candidate = cards[slot] as Card;
      if (candidate === selectedCard) exact.push(slot);
      else if (
        rankOf(candidate) === rankOf(selectedCard)
        || isWildcard(candidate, level)
        || isWildcard(selectedCard, level)
      ) compatible.push(slot);
    }
    for (const slot of [...exact, ...compatible]) {
      used.add(slot);
      const next = [...cards];
      next[slot] = selectedCard;
      visit(selectedIndex + 1, next);
      used.delete(slot);
      if (answer !== null) return;
    }
  };

  visit(0, base);
  return answer;
}

function compareOptions(a: SelectionOption, b: SelectionOption): number {
  if (a.active !== b.active) return a.active ? -1 : 1;
  const aBomb = a.combo.power > 0 ? 1 : 0;
  const bBomb = b.combo.power > 0 ? 1 : 0;
  if (aBomb !== bBomb) return aBomb - bBomb;
  if (a.added !== b.added) return a.added - b.added;
  const type = TYPE_ORDER.indexOf(a.combo.type) - TYPE_ORDER.indexOf(b.combo.type);
  if (type !== 0) return type;
  if (a.combo.power !== b.combo.power) return a.combo.power - b.combo.power;
  if (a.combo.rank !== b.combo.rank) return a.combo.rank - b.combo.rank;
  return optionKey(a).localeCompare(optionKey(b));
}

export function selectionOptions(
  hand: readonly Card[],
  selected: readonly Card[],
  current: Combo | null,
  level: Rank,
  rules: Ruleset,
  limit = 4,
  candidates: readonly Combo[] = legalPlays(hand, current, level, rules),
): SelectionOption[] {
  if (selected.length === 0 || limit <= 0) return [];
  const options: SelectionOption[] = [];
  const direct = canBeat(selected, current, level, rules);
  if (direct !== null) {
    options.push({ cards: [...selected], combo: direct, active: true, added: 0 });
  }
  for (const representative of candidates) {
    const anchored = anchorCombo(representative, selected, current, level, rules);
    if (anchored === null) continue;
    options.push({
      cards: anchored.cards,
      combo: anchored.combo,
      active: sameCards(anchored.cards, selected),
      added: Math.max(0, anchored.cards.length - selected.length),
    });
  }

  const unique = new Map<string, SelectionOption>();
  for (const option of options) {
    const key = optionKey(option);
    const prior = unique.get(key);
    if (prior === undefined || (!prior.active && option.active)) unique.set(key, option);
  }
  const ranked = [...unique.values()].sort(compareOptions);

  // Give the compact tray breadth before showing a second variation of the
  // same shape. The active choice always stays first.
  const chosen: SelectionOption[] = [];
  const usedTypes = new Set<ComboType>();
  for (const option of ranked) {
    if (chosen.length >= limit) break;
    if (usedTypes.has(option.combo.type)) continue;
    chosen.push(option);
    usedTypes.add(option.combo.type);
  }
  for (const option of ranked) {
    if (chosen.length >= limit) break;
    if (!chosen.includes(option)) chosen.push(option);
  }
  return chosen;
}
